import { Globe, Bot, ShoppingCart, Shield, Users, Building2, Zap, Network, Settings, Database, Server } from 'lucide-react';
import type { Product } from './types';
import centurionSecurityOpsCenter from '@/assets/centurion-security-ops-center.jpg';

export const coreXProducts: Product[] = [
  {
    id: 'centurion-global-protect',
    title: 'Centurion Global Protect',
    subtitle: 'Enterprise cybersecurity platform',
    description: 'Advanced enterprise cybersecurity platform with AI-powered threat detection, zero-trust architecture, and comprehensive compliance management for mission-critical business protection.',
    icon: Shield,
    image: centurionSecurityOpsCenter,
    category: 'Core Applications',
    status: 'Live',
    features: [
      'AI-powered threat detection',
      'Zero-trust security architecture',
      'Real-time security monitoring',
      'Advanced penetration testing',
      'Quantum-resistant encryption',
      'Automated incident response',
      'SOC 2 & ISO 27001 compliance',
      'Global threat intelligence',
      'Security performance analytics',
      'Scalable security infrastructure',
      '24/7 security operations center',
      'Continuous vulnerability assessment'
    ],
    pricing: { setup: 64000, monthly: 2944 },
    color: 'from-red-500 to-orange-600',
    businessTypes: ['#cybersecurity', '#enterprise-security', '#threat-detection', '#compliance', '#zero-trust'],
    demoConfig: {
      type: 'interactive',
      url: '/centurion-global-protect/demo',
      title: 'Centurion Global Protect Demo',
      description: 'Experience our AI-powered enterprise cybersecurity platform with real-time threat detection, zero-trust architecture, and comprehensive security operations.',
      buttonText: 'Try Live Demo',
      icon: Shield
    }
  },
  {
    id: 'security-compliance-suite',
    title: 'Security & Compliance Suite',
    subtitle: 'Enterprise-grade security platform',
    description: 'Comprehensive security and compliance management system with advanced threat detection, automated compliance reporting, and enterprise-grade security protocols.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Core Applications',
    status: 'Available',
    features: [
      'Advanced threat detection',
      'Automated compliance reporting',
      'Multi-factor authentication',
      'Security analytics dashboard',
      'Incident response automation',
      'Vulnerability management',
      'Compliance templates',
      '24/7 security monitoring'
    ],
    pricing: { setup: 38400, monthly: 1766 },
    color: 'from-red-500 to-orange-600',
    businessTypes: ['#security', '#compliance', '#enterprise-security', '#risk-management', '#cybersecurity']
  },
  {
    id: 'website-webapp-suite',
    title: 'Website & Web App Suite',
    subtitle: 'Complete digital presence platform',
    description: 'Full-featured website and web application development platform with modern design templates, e-commerce integration, and performance optimisation.',
    icon: Globe,
    image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Core Applications',
    status: 'Live',
    features: [
      'Modern responsive designs',
      'E-commerce integration',
      'SEO optimisation',
      'Performance monitoring',
      'Content management system',
      'Analytics dashboard',
      'Mobile optimisation',
      'Custom domain support'
    ],
    pricing: { setup: 30720, monthly: 1413 },
    color: 'from-blue-500 to-purple-600',
    businessTypes: ['#web-development', '#ecommerce', '#digital-presence', '#responsive-design', '#seo']
  },
  {
    id: 'communication-collaboration-hub',
    title: 'Communication & Collaboration Hub',
    subtitle: 'Unified team collaboration',
    description: 'Advanced team communication and collaboration platform with video conferencing, project management, and real-time collaboration tools.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Core Applications',
    status: 'Available',
    features: [
      'Video conferencing',
      'Real-time messaging',
      'File sharing & storage',
      'Project management tools',
      'Screen sharing',
      'Calendar integration',
      'Mobile applications',
      'Third-party integrations'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-teal-600',
    businessTypes: ['#collaboration', '#communication', '#remote-work', '#team-management', '#productivity']
  },
  {
    id: 'enterprise-resource-planning',
    title: 'Enterprise Resource Planning (ERP) Suite',
    subtitle: 'Complete business process management',
    description: 'Comprehensive ERP solution with financial management, human resources, supply chain integration, and multi-subsidiary support for enterprise operations.',
    icon: Building2,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Core Applications',
    status: 'Available',
    features: [
      'Financial management',
      'Human resources module',
      'Supply chain integration',
      'Multi-subsidiary support',
      'Real-time reporting',
      'Workflow automation',
      'Inventory management',
      'Business intelligence'
    ],
    pricing: { setup: 89600, monthly: 4122 },
    color: 'from-indigo-500 to-purple-600',
    businessTypes: ['#erp', '#enterprise-software', '#business-management', '#financial-management', '#hr-systems']
  },
  {
    id: 'digital-transformation-platform',
    title: 'Digital Transformation Platform',
    subtitle: 'Legacy system modernisation',
    description: 'Complete digital transformation solution with legacy system modernisation, cloud migration services, and process optimisation for enterprise digital evolution.',
    icon: Zap,
    image: '/src/assets/digital-transformation-hero.jpg',
    category: 'Core Applications',
    status: 'Available',
    features: [
      'Legacy system modernisation',
      'Cloud migration services',
      'Process optimisation',
      'API integration layer',
      'Data transformation tools',
      'Change management support',
      'Training and adoption',
      'Performance monitoring'
    ],
    pricing: { setup: 71680, monthly: 3297 },
    color: 'from-cyan-500 to-blue-600',
    businessTypes: ['#digital-transformation', '#cloud-migration', '#modernisation', '#process-optimisation', '#enterprise-evolution']
  },
  {
    id: 'enterprise-integration-hub',
    title: 'Enterprise Integration Hub',
    subtitle: 'API management and orchestration',
    description: 'Advanced integration platform with API management, real-time data synchronization, and microservices architecture for seamless enterprise connectivity.',
    icon: Network,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Core Applications',
    status: 'Available',
    features: [
      'API management portal',
      'Real-time data sync',
      'Microservices orchestration',
      'Message queue systems',
      'Event-driven architecture',
      'Integration monitoring',
      'Security gateway',
      'Developer tools'
    ],
    pricing: { setup: 64000, monthly: 2944 },
    color: 'from-violet-500 to-purple-600',
    businessTypes: ['#api-management', '#integration', '#microservices', '#data-synchronization', '#enterprise-architecture']
  },
  {
    id: 'business-process-automation',
    title: 'Business Process Automation Platform',
    subtitle: 'Workflow automation engine',
    description: 'Comprehensive process automation platform with workflow engine, RPA capabilities, and process intelligence for operational efficiency.',
    icon: Settings,
    image: 'https://images.unsplash.com/photo-1551808525-51a94da548ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Core Applications',
    status: 'Available',
    features: [
      'Workflow automation engine',
      'RPA bot management',
      'Process intelligence',
      'Rule-based automation',
      'Integration connectors',
      'Performance analytics',
      'Exception handling',
      'Audit trail'
    ],
    pricing: { setup: 56320, monthly: 2591 },
    color: 'from-amber-500 to-orange-600',
    businessTypes: ['#process-automation', '#rpa', '#workflow-management', '#operational-efficiency', '#business-optimisation']
  },
  {
    id: 'enterprise-data-management',
    title: 'Enterprise Data Management Platform',
    subtitle: 'Data governance and quality',
    description: 'Complete data management solution with data governance, quality management, master data management, and data catalog capabilities.',
    icon: Database,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Core Applications',
    status: 'Available',
    features: [
      'Data governance framework',
      'Quality management',
      'Master data management',
      'Data catalog and lineage',
      'Privacy compliance',
      'Data stewardship tools',
      'Metadata management',
      'Data profiling'
    ],
    pricing: { setup: 76800, monthly: 3533 },
    color: 'from-emerald-500 to-teal-600',
    businessTypes: ['#data-management', '#data-governance', '#data-quality', '#master-data', '#compliance']
  },
  {
    id: 'mission-critical-infrastructure',
    title: 'Mission-Critical Infrastructure Suite',
    subtitle: 'High-availability systems',
    description: 'Enterprise-grade infrastructure platform with high-availability systems, automated disaster recovery, and 24/7 monitoring for mission-critical operations.',
    icon: Server,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Core Applications',
    status: 'Available',
    features: [
      'High-availability architecture',
      'Automated disaster recovery',
      '24/7 monitoring and alerting',
      'Performance optimisation',
      'Scalability management',
      'Security hardening',
      'Backup and restoration',
      'Compliance reporting'
    ],
    pricing: { setup: 102400, monthly: 4710 },
    color: 'from-slate-500 to-zinc-600',
    businessTypes: ['#infrastructure', '#high-availability', '#disaster-recovery', '#monitoring', '#enterprise-systems']
  }
];
